/**
 * useProductionSettings - Production workspace settings with localStorage persistence
 *
 * Manages production-specific settings that persist across sessions:
 * - showDevTools: Show/hide QR test panel and other dev utilities
 * - pickingMode: batch_pick vs pick_by_line
 * - defaultBatchSize: Default batch size for auto-batch
 * - showPickingScanner: Show/hide QR scanner on batch detail page
 * - pretreatEnabled: Enable pretreat station in workflow
 * - packerPriorityEnabled: Enable packer priority system
 *
 * Uses localStorage for persistence with schema versioning for migrations.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import type { TabValue } from '@/features/production-issues/types';

const STORAGE_KEY = 'threadheads-production-settings';
const SETTINGS_VERSION = 1;

export interface ProductionSettings {
  /** Show QR test panel and other development tools */
  showDevTools: boolean;
  /** Picking mode: batch_pick (default) or pick_by_line */
  pickingMode: 'batch_pick' | 'pick_by_line';
  /** Default batch size for auto-batch */
  defaultBatchSize: number;
  /** Show QR scanner on batch detail page (picking verification) */
  showPickingScanner: boolean;
  /** Enable pretreat station in workflow */
  pretreatEnabled: boolean;
  /** Enable packer priority system */
  packerPriorityEnabled: boolean;
  /** Show OOS bins on bin dashboard */
  showOosBins: boolean;
  /**
   * Tabs hidden on the Production Issues page (per-user view preference).
   * Stored as a denylist so tabs added later default to visible. Empty = all shown.
   */
  hiddenIssueTabs: TabValue[];
}

interface StoredSettings {
  version: number;
  data: ProductionSettings;
}

/**
 * Default settings - conservative for production use
 * showDevTools defaults to false in production builds
 */
const DEFAULT_SETTINGS: ProductionSettings = {
  // Default OFF in production builds, ON in development for convenience
  showDevTools: import.meta.env.DEV,
  pickingMode: 'batch_pick',
  defaultBatchSize: 50,
  showPickingScanner: true,
  pretreatEnabled: true,
  packerPriorityEnabled: false,
  // orderQueuePageSize removed 2026-07-19: queue page size is the org-wide
  // prod_settings.order_queue_page_size — every consumer reads the DB value;
  // this browser copy was dead. Stored copies are ignored by the type merge.
  showOosBins: false,
  hiddenIssueTabs: [],
};

/**
 * Migrate settings from older versions to current version
 */
function migrateSettings(data: Partial<ProductionSettings>, fromVersion: number): ProductionSettings {
  // Currently at version 1, no migrations needed yet
  // Future migrations can be added here as version increases
  if (fromVersion < 1) {
    // Example: handle pre-versioned settings
    return { ...DEFAULT_SETTINGS, ...data };
  }
  return { ...DEFAULT_SETTINGS, ...data };
}

function loadSettings(): ProductionSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);

      // Handle versioned storage format
      if (typeof parsed === 'object' && 'version' in parsed && 'data' in parsed) {
        const { version, data } = parsed as StoredSettings;
        if (version < SETTINGS_VERSION) {
          // Migration needed
          console.info(`[ProductionSettings] Migrating from v${version} to v${SETTINGS_VERSION}`);
          return migrateSettings(data, version);
        }
        // Merge with defaults to handle new fields added in same version
        return { ...DEFAULT_SETTINGS, ...data };
      }

      // Handle legacy unversioned format (pre-versioning)
      console.info('[ProductionSettings] Migrating legacy unversioned settings');
      return migrateSettings(parsed, 0);
    }
  } catch {
    // Ignore parse errors, use defaults
    console.warn('[ProductionSettings] Failed to parse stored settings, using defaults');
  }
  return DEFAULT_SETTINGS;
}

function saveSettings(settings: ProductionSettings): void {
  try {
    const payload: StoredSettings = {
      version: SETTINGS_VERSION,
      data: settings,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage errors (e.g., quota exceeded)
    console.warn('[ProductionSettings] Failed to save settings to localStorage');
  }
}

export function useProductionSettings() {
  const [settings, setSettingsState] = useState<ProductionSettings>(loadSettings);

  // Persist to localStorage when settings change
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  // Update a single setting
  const updateSetting = useCallback(<K extends keyof ProductionSettings>(
    key: K,
    value: ProductionSettings[K]
  ) => {
    setSettingsState((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Update multiple settings at once
  const updateSettings = useCallback((updates: Partial<ProductionSettings>) => {
    setSettingsState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Reset to defaults
  const resetSettings = useCallback(() => {
    setSettingsState(DEFAULT_SETTINGS);
  }, []);

  // Toggle a boolean setting
  const toggleSetting = useCallback((key: keyof ProductionSettings) => {
    setSettingsState((prev) => {
      const current = prev[key];
      if (typeof current === 'boolean') {
        return { ...prev, [key]: !current };
      }
      return prev;
    });
  }, []);

  return useMemo(() => ({
    settings,
    updateSetting,
    updateSettings,
    resetSettings,
    toggleSetting,
  }), [settings, updateSetting, updateSettings, resetSettings, toggleSetting]);
}
